import {useAtomValue} from "jotai";
import {graphClientAtom} from "../store.ts";
import {useMutation, UseMutationOptions} from "@tanstack/react-query";
import {Team, User} from "./types.ts";

export function useTeamMembersMutation(
    options?: Omit<UseMutationOptions<User[], Error, Team[], unknown>, 'mutationFn'>
) {
  const graphApiClient = useAtomValue(graphClientAtom);

  return useMutation({
    mutationFn: async (teams: Team[]) => {
      if (!graphApiClient || teams.length === 0) {
        return [];
      }

      const userMap = new Map<string, User>();
      const teamSizeMap = new Map<string, number>();

      // Fetch members for each team
      const fetchPromises = teams.map(async team => {
        try {
          const response = await graphApiClient.api(`/teams/${team.id}/members`).get();
          const teamMembers = response.value || [];

          teamSizeMap.set(team.id, teamMembers.length);

          // Process each team member
          teamMembers.forEach((member: any) => {
            const userId = member.userId || member.id;

            if (userMap.has(userId)) {
              // User already exists, add this team to their teams
              const existingUser = userMap.get(userId)!;
              existingUser.teams.push(team);
              userMap.set(userId, existingUser);
            } else {
              // Create new user entry
              userMap.set(userId, {
                id: userId,
                displayName: member.displayName || 'Unknown',
                teams: [team],
                assignedTeam: team,
                userPrincipalName: member.email || member.userPrincipalName,
              });
            }
          });
        } catch (error) {
          console.error(`Error processing team ${team.displayName}:`, error);
        }
      });

      await Promise.all(fetchPromises);

      // Assign users to their smallest team
      userMap.forEach((user, _) => {
        if (user.teams.length > 1) {
          // Find the smallest team this user belongs to
          let smallestTeam = user.teams[0];
          let smallestSize = teamSizeMap.get(smallestTeam.id) || Number.MAX_SAFE_INTEGER;

          for (const team of user.teams) {
            const size = teamSizeMap.get(team.id) || Number.MAX_SAFE_INTEGER;
            if (size < smallestSize) {
              smallestSize = size;
              smallestTeam = team;
            }
          }

          user.assignedTeam = smallestTeam;
        }
      });

      // Return sorted list of unique users
      return Array.from(userMap.values())
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, ...options
  });
}
